import { useState } from 'react';
import { Link } from 'react-router-dom';
import DocsSidebar from './components/DocsSidebar';
import EndpointCard from './components/EndpointCard';
import CodeBlock from './components/CodeBlock';
import { endpoints, errorCodes, rateLimits, baseUrl } from '@/mocks/docs';
import BrandLogo from '@/components/common/BrandLogo';

const quickstartCurl = `# 1. Register a member
curl -X POST ${baseUrl}/loyalty-member \\
  -H "Authorization: Bearer lb_live_9f3k2..." \\
  -H "Content-Type: application/json" \\
  -d '{"external_id":"user_48213","full_name":"Ava Thompson","email":"ava@example.com"}'

# 2. Award points
curl -X POST ${baseUrl}/loyalty-earn \\
  -H "Authorization: Bearer lb_live_9f3k2..." \\
  -H "Content-Type: application/json" \\
  -d '{"member_id":"mem_8f3k2","points":150,"reason":"purchase_completed"}'

# 3. Read the balance
curl "${baseUrl}/loyalty-balance?member_id=mem_8f3k2" \\
  -H "Authorization: Bearer lb_live_9f3k2..."`;

const authSample = `Authorization: Bearer lb_live_9f3k2a1b2c3d4e5f6...`;

export default function DocsPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background-50">
      <header className="sticky top-0 z-30 h-16 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 flex items-center gap-3 px-4 md:px-8">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md bg-background-100 text-foreground-900 cursor-pointer"
          aria-label="Open menu"
        >
          <i className="ri-menu-line text-xl"></i>
        </button>

        <Link to="/" className="flex items-center gap-2.5">
          <BrandLogo className="w-8 h-8" />
          <span className="font-heading font-bold text-foreground-950">LoyaltyBridge</span>
          <span className="hidden sm:inline-block ml-1 text-xs font-medium px-2 py-0.5 rounded-full bg-background-100 border border-background-200 text-foreground-500">
            API docs
          </span>
        </Link>

        <div className="flex-1"></div>

        <div className="hidden sm:flex items-center gap-3">
          <Link
            to="/"
            className="whitespace-nowrap text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors"
          >
            Back to website
          </Link>
          <Link
            to="/dashboard"
            className="whitespace-nowrap text-sm font-semibold px-4 py-2 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            Dashboard
          </Link>
        </div>
      </header>

      <div className="flex">
        <DocsSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <main className="flex-1 min-w-0 px-4 md:px-8 lg:px-12 py-8 md:py-12 max-w-4xl">
          <div className="max-w-3xl">
            <span className="inline-block text-sm font-semibold text-accent-700 bg-accent-100 px-3 py-1 rounded-full">
              v1.0 reference
            </span>
            <h1 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-foreground-950">
              LoyaltyBridge API
            </h1>
            <p className="mt-3 text-foreground-600 leading-relaxed">
              Five endpoints for members, points, balances, and redemptions. One secure key lets any platform plug into
              your loyalty program in an afternoon.
            </p>
          </div>

          <section id="quickstart" className="scroll-mt-24 mt-12">
            <h2 className="font-heading font-semibold text-xl text-foreground-950">Quickstart</h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              You only need three calls to go from zero to a working loyalty program: register a member, award points,
              and read their balance back.
            </p>
            <div className="mt-5">
              <CodeBlock code={quickstartCurl} language="bash" title="quickstart.sh" />
            </div>
          </section>

          <section id="authentication" className="scroll-mt-24 mt-12">
            <h2 className="font-heading font-semibold text-xl text-foreground-950">Authentication</h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              Every request is authenticated with a scoped API key sent as a Bearer token. Keys are created in the
              dashboard, scoped to <span className="font-mono text-foreground-800">earn</span>,{' '}
              <span className="font-mono text-foreground-800">redeem</span>, or{' '}
              <span className="font-mono text-foreground-800">read</span>, and are never stored in plaintext.
            </p>
            <div className="mt-5">
              <CodeBlock code={authSample} language="http" title="request headers" />
            </div>
          </section>

          <section id="endpoints" className="scroll-mt-24 mt-12">
            <h2 className="font-heading font-semibold text-xl text-foreground-950">API reference</h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              The full set of endpoints, with request and response examples plus copy-paste code for cURL and
              JavaScript.
            </p>
            <div className="mt-8 flex flex-col gap-12">
              {endpoints.map((endpoint) => (
                <EndpointCard key={endpoint.id} endpoint={endpoint} />
              ))}
            </div>
          </section>

          <section id="errors" className="scroll-mt-24 mt-12">
            <h2 className="font-heading font-semibold text-xl text-foreground-950">Errors &amp; rate limits</h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              The API returns standard HTTP status codes and a JSON error body. Here are the codes you&apos;ll most
              commonly encounter.
            </p>

            <div className="mt-6 bg-background-50 border border-background-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100 text-left text-xs font-semibold uppercase tracking-wider text-foreground-500">
                    <th className="px-5 py-3.5">Code</th>
                    <th className="px-5 py-3.5">Title</th>
                    <th className="px-5 py-3.5">Meaning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/70">
                  {errorCodes.map((error) => (
                    <tr key={error.code} className="hover:bg-background-100/70 transition-colors">
                      <td className="px-5 py-3.5 font-mono text-xs font-semibold text-secondary-700 whitespace-nowrap">
                        {error.code}
                      </td>
                      <td className="px-5 py-3.5 font-medium text-foreground-950 whitespace-nowrap">{error.title}</td>
                      <td className="px-5 py-3.5 text-foreground-600">{error.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="mt-8 font-heading font-semibold text-lg text-foreground-950">Rate limits</h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {rateLimits.map((limit) => (
                <div key={limit.plan} className="bg-background-50 border border-background-200 rounded-lg p-4">
                  <p className="font-heading font-semibold text-foreground-950">{limit.plan}</p>
                  <p className="mt-2 text-sm text-foreground-600">{limit.requests}</p>
                  <p className="text-sm text-foreground-500">{limit.burst}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-12 p-6 rounded-lg bg-primary-500 text-background-50">
            <h2 className="font-heading font-semibold text-lg">Ready to build?</h2>
            <p className="mt-2 text-sm text-background-50/90 leading-relaxed">
              Generate a scoped API key in your dashboard and make your first call in minutes.
            </p>
            <Link
              to="/dashboard/integrations"
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-md bg-background-50 text-foreground-950 text-sm font-semibold hover:bg-background-100 transition-colors"
            >
              <i className="ri-plug-line"></i> Create an API key
            </Link>
          </div>
        </main>
      </div>
    </div>
  );
}
